/**
 * ReviewStep Component
 *
 * Seventh and final step: Review all entered information before submission.
 * Displays a comprehensive summary with edit buttons to go back to each section.
 */

import { CheckCircle2, Edit } from 'lucide-react';
import type { IOnboardingApplication } from '@order-up/models';
import type { AdditionalOwner } from './OwnersStep';
import type { OnboardingApplicationData } from '../../types/onboarding';

export interface ReviewStepProps {
  application: IOnboardingApplication;
  onEdit: (stepIndex: number) => void;
}

const OWNERSHIP_TYPE_LABELS: Record<string, string> = {
  'INDIVSOLE': 'Sole Proprietorship',
  'LLC': 'LLC',
  'PRIVCORP': 'Corporation',
  'PRTNRSHP': 'Partnership',
  'LLP': 'LLP',
  'NONPROFIT': 'Non-Profit'
};

export function ReviewStep({ application, onEdit }: ReviewStepProps) {
  const data = application.applicationData as OnboardingApplicationData;
  const isSoleProp = data.ownership?.ownershipTypeCd === 'INDIVSOLE';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-white">Review Your Application</h2>
        <p className="text-zinc-400">
          Please review all information carefully before submitting. You can edit any section by
          clicking the edit button.
        </p>
      </div>

      {/* Business Type */}
      <ReviewSection
        title="Business Type"
        onEdit={() => onEdit(0)}
        items={[
          {
            label: 'Entity Type',
            value: OWNERSHIP_TYPE_LABELS[data.ownership?.ownershipTypeCd || ''] || 'Not specified'
          }
        ]}
      />

      {/* Personal Information */}
      <ReviewSection
        title="Personal Information"
        onEdit={() => onEdit(1)}
        items={[
          { label: 'Full Name', value: data.ownership?.owner?.ownerName || 'Not provided' },
          { label: 'Email', value: data.ownership?.owner?.ownerEmail || 'Not provided' },
          { label: 'Date of Birth', value: data.ownership?.owner?.ownerDob || 'Not provided' },
          {
            label: 'Address',
            value: (() => {
              const a = data.ownership?.owner?.ownerAddress;
              if (!a) return 'Not provided';
              const line1 = [a.address1, a.address2].filter(Boolean).join(', ');
              return `${line1}, ${a.city || ''}, ${a.stateCd || ''} ${a.zip || ''}`;
            })()
          }
        ]}
      />

      {/* Business Information */}
      <ReviewSection
        title="Business Information"
        onEdit={() => onEdit(2)}
        items={[
          { label: 'Legal Business Name', value: data.legalBusinessName || 'Not provided' },
          { label: 'Tax Filing Name (DBA)', value: data.taxFilingName || 'Same as legal name' },
          { label: 'Business Phone', value: data.demographic?.businessPhone || 'Not provided' },
          {
            label: 'Business Address',
            value: data.demographic?.businessAddress
              ? `${data.demographic.businessAddress.address1 || ''}, ${data.demographic.businessAddress.city || ''}, ${data.demographic.businessAddress.stateCd || ''} ${data.demographic.businessAddress.zip || ''}`
              : 'Not provided'
          }
        ]}
      />

      {/* Additional Owners */}
      {!isSoleProp && (
        <ReviewSection
          title="Additional Owners (25%+ stake)"
          onEdit={() => onEdit(3)}
          items={
            data.ownership?.additionalOwners && (data.ownership.additionalOwners as AdditionalOwner[]).length > 0
              ? (data.ownership.additionalOwners as AdditionalOwner[]).map((owner, idx) => ({
                  label: `Owner ${idx + 2}`,
                  value: `${owner.ownerName} (${owner.ownershipPercentage}%)`
                }))
              : [{ label: 'Additional Owners', value: 'None' }]
          }
        />
      )}

      {/* Submission Notice */}
      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-6">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 text-emerald-400 shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="font-medium text-emerald-300">Ready to Submit</p>
            <p className="text-sm text-emerald-200/80">
              By clicking "Submit Application" below, you certify that all information provided is
              accurate and complete. Your application will be reviewed by our underwriting team,
              typically within 1-2 business days.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Review Section Component
interface ReviewSectionProps {
  title: string;
  onEdit: () => void;
  items: Array<{ label: string; value: string }>;
}

function ReviewSection({ title, onEdit, items }: ReviewSectionProps) {
  return (
    <div className="rounded-xl border border-border bg-zinc-900/50 p-6">
      {/* Header with edit button */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/50">
        <h3 className="text-lg font-medium text-white">{title}</h3>
        <button
          type="button"
          onClick={onEdit}
          className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
        >
          <Edit className="h-4 w-4" />
          Edit
        </button>
      </div>

      {/* Items */}
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={index} className="flex justify-between gap-4">
            <span className="text-sm text-zinc-400">{item.label}:</span>
            <span className="text-sm text-white font-medium text-right">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
